import classNames from 'classnames';
import React, {ReactElement, useCallback, useState} from 'react';

import Col from '../../common/Col';
import Row from '../../common/Row';
import {GameScore, useAddGameScoreMutation} from '../../query/graphql';
import {useAppDispatch} from '../../store';
import {Actions} from '../gameSlice';
import {useGameStatus} from '../hooks';
import {LeaderIconId, LEADERS} from '../resources/leaderIcons';
import ArrowButton from './buttons/ArrowButton';
import DotsLoader from './DotsLoader';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';

type Props = {
  onSubmitted: (myScore: GameScore, scores: GameScore[]) => void;
};

const LeaderboardAdd = ({onSubmitted}: Props): ReactElement | null => {
  const dispatch = useAppDispatch();
  const addScore = useAddGameScoreMutation();
  const gameStatus = useGameStatus();

  const [selectedIcon, setSelectedIcon] = useState<LeaderIconId>();
  const [name, setName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const valid = selectedIcon && name.length === 3;

  const handleChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(
      e.target.value
        .split('')
        .filter(char => char.match(/[a-zA-Z0-9]/))
        .slice(0, 3)
        .join('')
        .toUpperCase()
    );
  }, []);

  const submitEntry = useCallback(async () => {
    if (!valid) return;
    setLoading(true);
    setError(undefined);
    try {
      const {addGameScore} = await addScore.mutateAsync({
        name,
        iconId: selectedIcon,
        money: gameStatus.money,
        production: gameStatus.totalProduction,
      });
      const myScore = addGameScore.entries.find(entry => entry.uuid === addGameScore.entryId)!;
      if (!myScore) throw new Error('Could not find my score in the leaderboard');
      dispatch(Actions.setScoreAdded(true));
      onSubmitted(myScore, addGameScore.entries);
    } catch (e) {
      setError((e as any)?.message ?? String(e) ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [addScore, dispatch, gameStatus.money, gameStatus.totalProduction, name, onSubmitted, selectedIcon, valid]);

  return (
    <MessageBox
      dark
      top={
        <SimplePill className={'!pr-xs !flex !flex-row gap-md'}>
          Leaderboard <span className="text-base text-primary-800">1/2</span>
        </SimplePill>
      }
      bottom={
        <Row className={'justify-center'}>
          <ArrowButton direction={'right'} className={'!mx-auto'} onClick={submitEntry} disabled={!valid || loading}>
            Add to leaderboard
          </ArrowButton>
        </Row>
      }
    >
      <Col className={'mt-xs p-sm gap-md items-center text-center'}>
        <div className={'font-semibold text-3xl'}>Add your score to the leaderboard</div>

        <Col className={classNames('w-full gap-md items-center', loading && 'opacity-0')}>
          <div className={'font-semibold text-2xl'}>Select an avatar</div>
          <ul className={'max-w-full mx-auto p-0 flex flex-row gap-xs overflow-auto'}>
            {Object.entries(LEADERS).map(([key, Icon]) => {
              return (
                <li key={key} className={'inline'}>
                  <button onClick={() => setSelectedIcon(key as LeaderIconId)}>
                    <Icon
                      className={classNames(
                        'h-[7rem] w-[7rem] border-normal rounded-full',
                        selectedIcon === key ? 'bg-primary-300 border-primary-300' : 'bg-gray-100 border-gray-100'
                      )}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
          <div className={'font-semibold text-2xl'}>Enter a 3 letter name or word</div>
          <input
            type="text"
            autoFocus
            value={name}
            className={'text-primary-800'}
            maxLength={3}
            onChange={handleChangeName}
          />
          {error && <div className={'text-red-200'}>{error}</div>}
        </Col>
        {loading && (
          <Col className={'absolute inset-0 items-center justify-center'}>
            <DotsLoader />
          </Col>
        )}
      </Col>
    </MessageBox>
  );
};

export default LeaderboardAdd;
